import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import { RenderOpenProps } from 'carbon-react/lib/components/popover-container';

import { PopoverMenu } from '@futrli/ui-components/components/popover-menu/popover-menu';
import { usePopoverRowShield } from '@futrli/ui-components/hooks/usePopoverRowShield';

import { COADisplayAccount } from '../../types';
import { DragHandlers } from '../../hooks/useCategoryDragDrop';
import { useCoaActions } from '../context/coa-actions-context';

import * as styles from './group-row-actions.scss';

interface Props {
  coaRow: COADisplayAccount;
  drag: DragHandlers;
  index: number;
  isUpdatingStructure: boolean;
}

export const GroupRowActions = ({ coaRow, drag, index, isUpdatingStructure }: Props) => {
  const { handleDeleteGroupClick, handleEditGroupClick } = useCoaActions();

  const attachShieldRef = usePopoverRowShield();

  const items = [
    {
      label: <FormattedMessage id="common.edit_group" defaultMessage="Edit group" />,
      onClick: () => handleEditGroupClick(coaRow)
    },
    {
      label: (
        <>
          <Icon type="delete" fontSize="small" />
          <FormattedMessage id="common.delete" defaultMessage="Delete" />
        </>
      ),
      onClick: () => handleDeleteGroupClick(coaRow),
      variant: 'danger' as const,
      hasBorder: true
    },
    ...(drag.canMoveUp(index)
      ? [
          {
            label: <FormattedMessage id="common.move_up" defaultMessage="Move up" />,
            onClick: () => drag.onMoveUp(index)
          }
        ]
      : []),
    ...(drag.canMoveDown(index)
      ? [
          {
            label: <FormattedMessage id="common.move_down" defaultMessage="Move down" />,
            onClick: () => drag.onMoveDown(index)
          }
        ]
      : []),
    ...(drag.canMoveToTop(index)
      ? [
          {
            label: <FormattedMessage id="common.move_to_top" defaultMessage="Move to top" />,
            onClick: () => drag.onMoveToTop(index)
          }
        ]
      : []),
    ...(drag.canMoveToBottom(index)
      ? [
          {
            label: <FormattedMessage id="common.move_to_bottom" defaultMessage="Move to bottom" />,
            onClick: () => drag.onMoveToBottom(index)
          }
        ]
      : [])
  ];

  return (
    <div
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        color: '#000',
        zIndex: 1000
      }}
      className={`${styles.darkFont} ${isUpdatingStructure ? styles.disabled : ''}`}
    >
      <PopoverMenu
        items={items}
        renderOpenComponent={({ onClick, ref, 'aria-expanded': ariaExpanded }: RenderOpenProps) => (
          <Button
            buttonType="tertiary"
            iconType="ellipsis_vertical"
            iconPosition="after"
            onClick={onClick}
            ref={attachShieldRef(ref)}
            aria-expanded={ariaExpanded}
            size="small"
          >
            <FormattedMessage id="predict.actions" defaultMessage="Actions" />
          </Button>
        )}
      />
    </div>
  );
};
